import { Maybe } from '@kinetic-ui/data-layer';
import { FileWriter, assertTruthy } from 'kineticUtils/data';

import { ColumnConfig, UpdateConfig } from './contexts/ColumnsContext';
import { BaseRow } from './types';
import { publicUrl } from 'kineticUtils/routing/urls';
import { isMoment } from 'moment';

export const orderColumns = (columns: ColumnConfig<BaseRow>[]) => {
  // numerically sort columns with order defined
  const columnsWithOrder = columns.filter(
    (column) => column.order !== undefined && column.order !== null
  ) as Required<ColumnConfig>[];
  columnsWithOrder.sort((a, b) => a.order - b.order);

  // grab only negative or out of bounds orders.
  const nonIndexColumns = columnsWithOrder.filter((col) => {
    const isAboveMin = col.order > -1;
    const isBelowMax = col.order < columns.length;
    return !(isAboveMin && isBelowMax);
  });

  // start with final orderedList with the negative orders
  const finalColumns: ColumnConfig<BaseRow, unknown>[] = nonIndexColumns.filter(
    (col) => col.order < 0
  );

  // grab only columns that are index-like
  const indexColumns = columnsWithOrder.filter((col) => {
    const isAboveMin = col.order > -1;
    const isBelowMax = col.order < columns.length;
    return isAboveMin && isBelowMax;
  });

  // Shuffle index-like columns into their positions
  // create an array of nulls, as placeholders for the middle columns.
  const middleColumns: Maybe<ColumnConfig<BaseRow>>[] = columns
    .filter(
      (col) =>
        indexColumns.map(({ name: indexedName }) => indexedName).includes(col.name) ||
        col.order === undefined
    )
    .map(() => null);
  // insert index-like columns at their indexes (SUPPORTS DUPLICATES)
  indexColumns.forEach((column) => {
    // if there's only a null it's the first column for an order
    const isFirst = !middleColumns[column.order];
    if (isFirst) {
      middleColumns[column.order] = column;
    }
  });
  // fill the empty spots with unordered columns
  columns
    .filter((column) => column.order === undefined)
    .forEach((column) => {
      const firstEmptyIndex = middleColumns.indexOf(null);
      middleColumns[firstEmptyIndex] = column;
    });

  const cleanedMiddleColumns = middleColumns.filter(assertTruthy);
  // push properly ordered middle columns into our final list
  finalColumns.push(...cleanedMiddleColumns);
  // finally insert out of bounds at the end
  finalColumns.push(...nonIndexColumns.filter((col) => col.order > cleanedMiddleColumns.length));

  return finalColumns;
};

/** grabs all safe column attributes for saving */
export const columnToJson = ({ name, visible, order }: ColumnConfig<BaseRow>): UpdateConfig => {
  return {
    name,
    visible,
    order,
  };
};

export const exportAsCSV = async (
  fileName: string,
  columns: ColumnConfig[],
  data: BaseRow[],
  additionalData?: string[]
) => {
  const visibleColumns = columns.filter((column) => column.visible);
  const headerRow: string[] = [];
  const columnKeys: string[] = [];
  const columnMap: Record<string, ColumnConfig> = {};
  const rows: string[] = [];
  visibleColumns.forEach((column) => {
    columnKeys.push(column.name);
    columnMap[column.name] = column;
    headerRow.push(column.title as string);
  });
  rows.push(...(additionalData ?? []));
  rows.push(headerRow.join(','));
  data?.forEach((record) => {
    const row: any[] = [];
    columnKeys.forEach((key) => {
      const column: ColumnConfig = columnMap[key];
      const getValue = column?.getValue?.current;
      const value = getValue ? getValue(record, true) : (record[key as keyof BaseRow] as string);
      if (typeof value === 'object' && isMoment(value)) {
        row.push('"' + column?.render(value, record, null) + '"');
      } else if (typeof value === 'string' || Array.isArray(value)) {
        row.push('"' + value + '"');
      } else {
        row.push(value);
      }
    });
    rows.push(row.join(','));
  });
  const csv = rows.join('\r\n');
  const fileWriter = await FileWriter.createFile({
    name: fileName,
    description: 'CSV File',
    contentType: 'text/csv',
    extension: '.csv',
  });
  fileWriter.write(csv);
  fileWriter.close();
};

interface RowNavigation {
  event: React.MouseEvent;
  id: string;
  route: (path?: string) => string;
  push: (path: string, state?: unknown) => void;
}

export const rowNavigation = ({ event, id, route, push }: RowNavigation) => {
  if (event.ctrlKey) {
    const path = `${window.location.origin}${publicUrl(route(id))}`;
    window.open(path);
    return;
  }
  push(route(`${id}`));
};
